/**
 * Invitations Screen
 *
 * Listagem de convites
 */
import React, { useState } from 'react'
import {
	CheckCircle,
	XCircle,
	Clock,
	Trash2,
} from 'lucide-react'
import Header from '../organisms/Header'
import DataTable from '../organisms/DataTable'
import FilterBar from '../molecules/FilterBar'
import { ConfirmModal } from '../molecules/Modal'
import Badge from '../atoms/Badge'
import Avatar from '../atoms/Avatar'
import { LoadingScreen } from '../atoms/Spinner'
import { invitationsApi } from '../../core/admin.api'
import { usePaginatedList } from '../../core/admin.hooks'
import { formatDate } from '../../core/admin.utils'
import toast from 'react-hot-toast'

const INVITATION_STATUS = {
	pending: { label: 'Pendente', color: 'yellow', icon: Clock },
	accepted: { label: 'Aceito', color: 'green', icon: CheckCircle },
	rejected: { label: 'Recusado', color: 'red', icon: XCircle },
	expired: { label: 'Expirado', color: 'gray', icon: XCircle },
}

export default function InvitationsScreen() {
	const [filters, setFilters] = useState({})
	const [selectedInvitation, setSelectedInvitation] = useState(null)
	const [showDeleteModal, setShowDeleteModal] = useState(false)
	const [actionLoading, setActionLoading] = useState(false)

	const {
		data,
		loading,
		page,
		setPage,
		setSearch,
		refetch,
	} = usePaginatedList(invitationsApi.list, filters)

	const invitations = data?.data || []
	const totalPages = data?.pagination?.totalPages || 1
	const total = data?.pagination?.total || 0

	// Filtros disponíveis
	const filterOptions = [
		{
			key: 'status',
			label: 'Status',
			type: 'select',
			options: [
				{ value: '', label: 'Todos' },
				{ value: 'pending', label: 'Pendente' },
				{ value: 'accepted', label: 'Aceito' },
				{ value: 'rejected', label: 'Recusado' },
				{ value: 'expired', label: 'Expirado' },
			],
		},
	]

	// Colunas da tabela
	const columns = [
		{
			key: 'invitation',
			label: 'Convite',
			render: (row) => {
				const statusInfo = INVITATION_STATUS[row.status] || INVITATION_STATUS.pending
				const Icon = statusInfo.icon

				// Mapear tipo para descrição
				const typeLabels = {
					invite: 'Convite para partida',
					request: 'Solicitação para partida',
					request_club: 'Solicitação para clube',
					CLUB_INVITE_USER: 'Convite para clube',
				}

				return (
					<div className="flex items-center gap-3">
						<div className={`p-2 bg-${statusInfo.color}-100 rounded-lg`}>
							<Icon className={`w-5 h-5 text-${statusInfo.color}-600`} />
						</div>
						<div>
							<p className="font-medium text-text">
								{typeLabels[row.type] || row.type}
							</p>
							<p className="text-sm text-text-body">
								{row.club?.name || row.match?.location?.name || 'N/A'}
							</p>
						</div>
					</div>
				)
			},
		},
		{
			key: 'user',
			label: 'Usuário',
			render: (row) => (
				<div className="flex items-center gap-2">
					<Avatar
						src={row.user?.avatar}
						name={row.user?.name}
						size="sm"
					/>
					<span className="text-text-body">
						{row.user?.name ? `${row.user.name} ${row.user.lastname || ''}` : 'N/A'}
					</span>
				</div>
			),
		},
		{
			key: 'status',
			label: 'Status',
			render: (row) => {
				const statusInfo = INVITATION_STATUS[row.status] || INVITATION_STATUS.pending
				return (
					<Badge variant={statusInfo.color}>
						{statusInfo.label}
					</Badge>
				)
			},
		},
		{
			key: 'createdAt',
			label: 'Enviado em',
			render: (row) => (
				<span className="text-text-body text-sm">
					{formatDate(row.createdAt, 'dd/MM/yyyy HH:mm')}
				</span>
			),
		},
	]

	// Ações do menu
	const getRowActions = (row) => [
		{
			icon: Trash2,
			label: 'Excluir',
			onClick: () => {
				setSelectedInvitation(row)
				setShowDeleteModal(true)
			},
			variant: 'danger',
		},
	]

	// Handler para excluir
	const handleDelete = async () => {
		if (!selectedInvitation) return

		setActionLoading(true)
		try {
			await invitationsApi.delete(selectedInvitation._id)
			toast.success('Convite excluído com sucesso')
			setShowDeleteModal(false)
			refetch()
		} catch (err) {
			toast.error(err.response?.data?.message || 'Erro ao excluir convite')
		} finally {
			setActionLoading(false)
		}
	}

	if (loading && !data) {
		return (
			<div className="min-h-screen">
				<Header title="Convites" />
				<LoadingScreen />
			</div>
		)
	}

	return (
		<div className="min-h-screen">
			<Header
				title="Convites"
				subtitle={`${total} convites enviados`}
			/>

			<div className="p-3 md:p-6 space-y-3 md:space-y-4">
				{/* Filtros */}
				<FilterBar
					filters={filterOptions}
					values={filters}
					onChange={setFilters}
					onSearch={setSearch}
					searchPlaceholder="Buscar por email, clube..."
				/>

				{/* Tabela */}
				<DataTable
					columns={columns}
					data={invitations}
					loading={loading}
					emptyMessage="Nenhum convite encontrado"
					page={page}
					totalPages={totalPages}
					onPageChange={setPage}
					actions={getRowActions}
				/>
			</div>

			{/* Modal de confirmação de exclusão */}
			<ConfirmModal
				isOpen={showDeleteModal}
				onClose={() => setShowDeleteModal(false)}
				onConfirm={handleDelete}
				title="Excluir Convite"
				message="Tem certeza que deseja excluir este convite? Esta ação não pode ser desfeita."
				confirmText="Excluir"
				variant="danger"
				loading={actionLoading}
			/>
		</div>
	)
}
